import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, Upload, X, Car, ShieldCheck } from "lucide-react";
import FleetReconciliation from "./FleetReconciliation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const blank = {
  year: "", make: "", model: "", color: "", license_plate: "", category: "Standard",
  seats: 5, bags: 3, byoi_day: 0, byoi_week: 0, allin_day: 0, allin_week: 0,
  blurb: "", status: "available", images: [], is_active: true, sort_order: 0, purchase_price: 0, odometer: 0,
};

const STATUS_OPTS = [
  { value: "available", label: "Available" },
  { value: "rented", label: "Rented" },
  { value: "maintenance", label: "Maintenance" },
  { value: "owner_use", label: "Owner Use / Unavailable" },
];

export default function FleetManager({ token, onAuthError }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showRecon, setShowRecon] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/vehicles?all=true`)
      .then((r) => setVehicles(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this vehicle permanently?")) return;
    try {
      await axios.delete(`${API}/vehicles/${id}`, auth);
      toast.success("Vehicle deleted.");
      load();
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired. Please log in again."); onAuthError?.(); }
      else toast.error(err.response?.data?.detail || "Could not delete.");
    }
  };

  const toggleReady = async (v) => {
    const turningOn = !v.ready_to_offer;
    if (turningOn && !window.confirm(`Mark the ${v.year} ${v.make} ${v.model} as READY TO OFFER?\n\nThis is the owner approval that allows waitlist/availability offers for this vehicle (it must ALSO be genuinely free — the server still blocks offers while any rental is out). Vehicles are NOT ready by default.`)) return;
    try {
      await axios.patch(`${API}/vehicles/${v.id}/ready-to-offer`, { ready: turningOn }, auth);
      toast.success(turningOn ? "Vehicle marked Ready to Offer." : "Ready to Offer turned OFF — this vehicle will not be offered.");
      load();
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired. Please log in again."); onAuthError?.(); }
      else toast.error(err.response?.data?.detail || "Could not update.");
    }
  };

  return (
    <div data-testid="admin-fleet-manager">
      <div className="flex items-center justify-between mb-5 gap-2 flex-wrap">
        <p className="text-white/50 text-sm">{vehicles.length} vehicle(s). Changes go live on the website instantly.</p>
        <div className="flex gap-2">
          <button data-testid="fleet-recon-open" onClick={() => setShowRecon(!showRecon)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs border ${showRecon ? "bg-amber-500/20 text-amber-300 border-amber-500/40 font-bold" : "border-white/15 text-white/60"}`}>
            <ShieldCheck size={13} /> Reconciliation Report
          </button>
          <button data-testid="fleet-add-vehicle" onClick={() => setEditing({ ...blank, sort_order: vehicles.length + 1 })}
            className="flex items-center gap-2 bg-[#F2B705] text-black font-bold px-4 py-2.5 rounded-md hover:bg-white transition-colors">
            <Plus size={16} /> Add Vehicle
          </button>
        </div>
      </div>

      {showRecon && <FleetReconciliation token={token} onClose={() => setShowRecon(false)} />}

      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vehicles.map((v) => (
            <div key={v.id} data-testid={`fleet-row-${v.id}`} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4 flex gap-4">
              <div className="w-28 h-20 rounded-md bg-black overflow-hidden shrink-0 flex items-center justify-center">
                {v.images?.[0] ? <img src={v.images[0]} alt="" className="w-full h-full object-cover" /> : <Car className="text-white/20" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-display font-bold truncate">{v.year} {v.make} {v.model}</h4>
                  <span data-testid={`fleet-status-badge-${v.id}`} className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${v.status === "available" ? "bg-green-500/15 text-green-400" : v.status === "rented" ? "bg-red-500/15 text-red-400" : v.status === "owner_use" ? "bg-sky-500/15 text-sky-300" : "bg-yellow-500/15 text-yellow-400"}`}>{v.status === "owner_use" ? "Owner Use" : v.status}</span>
                </div>
                <p className="text-white/45 text-xs mt-0.5">{v.color} · {v.category} · Plate {v.license_plate || "—"}</p>
                <p className="text-white/55 text-xs mt-1">BYOI ${v.byoi_day}/day · All-In ${v.allin_day}/day {!v.is_active && "· (hidden)"}</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <button data-testid={`fleet-edit-${v.id}`} onClick={() => setEditing(v)} className="flex items-center gap-1 text-xs border border-white/15 px-3 py-1.5 rounded hover:bg-white hover:text-black transition-colors"><Pencil size={12} /> Edit</button>
                  <button data-testid={`fleet-ready-toggle-${v.id}`} onClick={() => toggleReady(v)}
                    title="Owner approval required before this vehicle can be offered to waitlist customers"
                    className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded border transition-colors ${v.ready_to_offer ? "border-sky-500/50 text-sky-300 bg-sky-500/10" : "border-white/15 text-white/45 hover:text-white"}`}>
                    <ShieldCheck size={12} /> {v.ready_to_offer ? "Ready to Offer ✓" : "Not Ready to Offer"}
                  </button>
                  <button data-testid={`fleet-delete-${v.id}`} onClick={() => remove(v.id)} className="flex items-center gap-1 text-xs border border-red-500/30 text-red-400 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <VehicleForm vehicle={editing} token={token} onAuthError={onAuthError} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function VehicleForm({ vehicle, token, onClose, onSaved, onAuthError }) {
  const [f, setF] = useState(vehicle);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const isNew = !vehicle.id;
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const num = (k, v) => set(k, v === "" ? 0 : parseFloat(v));
  const cls = "w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-sm rounded-none";

  const addImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload?public=true`, fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      const url = data.url?.startsWith("http") ? data.url : `${BACKEND}${data.url}`;
      set("images", [...f.images, url]);
      toast.success("Photo added.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image upload failed. Try a smaller JPG/PNG.");
    }
    finally { setUploading(false); }
  };

  const removeImage = (idx) => set("images", f.images.filter((_, i) => i !== idx));

  const save = async () => {
    if (!f.make || !f.model) { toast.error("Make and model are required."); return; }
    setSaving(true);
    const payload = { ...f, seats: parseInt(f.seats) || 5, bags: parseInt(f.bags) || 3, sort_order: parseInt(f.sort_order) || 0, odometer: parseInt(f.odometer) || 0 };
    try {
      if (isNew) await axios.post(`${API}/vehicles`, payload, auth);
      else await axios.put(`${API}/vehicles/${vehicle.id}`, payload, auth);
      toast.success(isNew ? "Vehicle added!" : "Vehicle updated!");
      onSaved();
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired. Please log in again."); onAuthError?.(); }
      else toast.error(err.response?.data?.detail || "Could not save vehicle.");
    }
    finally { setSaving(false); }
  };

  const field = (label, key, type = "text") => (
    <div><label className="text-xs text-white/45">{label}</label>
      <input type={type} className={`${cls} ${type === "date" ? "[color-scheme:dark]" : ""}`} value={f[key]} onChange={(e) => (type === "number" ? num(key, e.target.value) : set(key, e.target.value))} /></div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0C0C0C] border border-white/12 rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="vehicle-form">
        <h3 className="font-display font-bold text-xl">{isNew ? "Add Vehicle" : "Edit Vehicle"}</h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-5">
          {field("Year", "year")}
          {field("Make", "make")}
          {field("Model", "model")}
          {field("Color", "color")}
          {field("License Plate", "license_plate")}
          <div><label className="text-xs text-white/45">Status</label>
            <select data-testid="vehicle-status" className={`${cls} appearance-none`} value={f.status} onChange={(e) => set("status", e.target.value)}>
              {STATUS_OPTS.map((s) => <option key={s.value} value={s.value} className="bg-[#0A0A0A]">{s.label}</option>)}
            </select>
          </div>
          {field("Category", "category")}
          {field("Seats", "seats", "number")}
          {field("Bags", "bags", "number")}
          {field("BYOI $/day", "byoi_day", "number")}
          {field("BYOI $/week", "byoi_week", "number")}
          <div className="col-span-2 text-white/40 text-xs bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 self-end" data-testid="allin-derived-note">
            All-In rates display automatically: BYOI rate + company coverage rate (set in Money → Pricing Settings).
          </div>
          {field("Purchase price $", "purchase_price", "number")}
          {field("Sort order", "sort_order", "number")}
          {field("Odometer (miles)", "odometer", "number")}
        </div>

        <div className="mt-4"><label className="text-xs text-white/45">Description</label>
          <textarea rows={2} className={cls} value={f.blurb} onChange={(e) => set("blurb", e.target.value)} /></div>

        <label className="flex items-center gap-2 mt-4 cursor-pointer text-sm text-white/70">
          <input type="checkbox" checked={f.is_active} onChange={(e) => set("is_active", e.target.checked)} className="h-4 w-4 accent-[#F2B705]" />
          Show on website
        </label>

        {/* Premium Package */}
        <div className="mt-5 border border-[#F2B705]/30 rounded-lg p-4" data-testid="vehicle-premium-section">
          <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-white">
            <input type="checkbox" data-testid="vehicle-premium-toggle" checked={!!(f.premium && f.premium.enabled)}
              onChange={(e) => set("premium", { package_price: 499, exact_days: 3, deposit: 750, included_miles: 300, excess_mile_rate: 0.75, block_extensions: true, require_approval: true, disclaimer: "", ...(f.premium || {}), enabled: e.target.checked })}
              className="h-4 w-4 accent-[#F2B705]" />
            ⭐ Premium Package Vehicle (fixed-length rental)
          </label>
          {f.premium?.enabled && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                {[["Package price $", "package_price"], ["Package days (minimum)", "exact_days"], ["Extra day $/day (blank = fixed-length only)", "extra_day_rate"], ["Security deposit $", "deposit"],
                  ["Included miles", "included_miles"], ["Excess $/mile", "excess_mile_rate"]].map(([label, key]) => (
                  <div key={key}><label className="text-xs text-white/45">{label}</label>
                    <input type="number" step="0.01" data-testid={`vehicle-premium-${key}`} className={cls} value={f.premium[key] ?? ""}
                      onChange={(e) => set("premium", { ...f.premium, [key]: e.target.value === "" ? "" : parseFloat(e.target.value) })} /></div>
                ))}
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer text-xs text-white/70">
                <input type="checkbox" data-testid="vehicle-premium-block-ext" checked={f.premium.block_extensions !== false}                  onChange={(e) => set("premium", { ...f.premium, block_extensions: e.target.checked })} className="h-4 w-4 accent-[#F2B705]" />
                Block customer-requested online extensions (management exceptions only)
              </label>
              <div className="mt-3"><label className="text-xs text-white/45">Public disclaimer (shown before booking)</label>
                <textarea rows={2} data-testid="vehicle-premium-disclaimer" className={cls} placeholder="Three-Day Rental Only — Vehicle must be returned on the scheduled return date. No weekly rentals or customer-requested extensions."
                  value={f.premium.disclaimer || ""} onChange={(e) => set("premium", { ...f.premium, disclaimer: e.target.value })} /></div>
              <div className="mt-3 border-t border-white/10 pt-3">
                <div className="text-xs font-bold text-[#F2B705] uppercase tracking-wide mb-2">Second Package (optional — e.g. Weekly $400 / 7 days)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[11px] text-white/45">Package 2 price $</label>
                    <input type="number" step="0.01" data-testid="vehicle-premium-pkg2-price" className={cls}
                      value={f.premium?.packages?.[0]?.price ?? ""}
                      onChange={(e) => set("premium", { ...f.premium, packages: e.target.value === "" ? [] : [{ ...(f.premium.packages?.[0] || { days: 7 }), price: parseFloat(e.target.value) }] })} /></div>
                  <div><label className="text-[11px] text-white/45">Package 2 length (days)</label>
                    <input type="number" data-testid="vehicle-premium-pkg2-days" className={cls}
                      value={f.premium?.packages?.[0]?.days ?? ""}
                      onChange={(e) => set("premium", { ...f.premium, packages: [{ ...(f.premium.packages?.[0] || { price: "" }), days: e.target.value === "" ? "" : parseInt(e.target.value) }] })} /></div>
                </div>
                <p className="text-white/35 text-[11px] mt-1.5">With both packages set, customers can book EITHER length (e.g. 3-day $333 or weekly $400) — nothing in between, and regular promos/specials never apply.</p>
              </div>
              <p className="text-white/35 text-[11px] mt-2">With an <b className="text-white/60">Extra day rate</b> set, customers can book any length: package price for the first days, then the premium per-day rate (weekly package applies at 7+ days) — like your standard cars but at premium prices. Leave it blank for strict fixed-length rentals only. Regular specials/promos never apply either way. Every reservation still requires management approval before confirmation.</p>
            </>
          )}
        </div>

        {/* Images */}
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-widest text-white/45">Photos</label>
            <button type="button" data-testid="vehicle-add-photo" onClick={() => fileRef.current?.click()} className="flex items-center gap-1 text-xs border border-white/15 px-3 py-1.5 rounded hover:bg-white hover:text-black transition-colors">
              {uploading ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />} Add Photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={addImage} />
          </div>
          <div className="flex flex-wrap gap-3 mt-3">
            {f.images.length === 0 && <p className="text-white/30 text-sm">No photos yet.</p>}
            {f.images.map((img, i) => (
              <div key={i} className="relative w-28 h-20 rounded-md overflow-hidden border border-white/15 bg-black">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button data-testid={`vehicle-remove-photo-${i}`} onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/70 rounded-full p-1 hover:bg-red-500"><X size={12} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 border border-white/15 py-2.5 rounded-md hover:bg-white/5">Cancel</button>
          <button data-testid="vehicle-save" onClick={save} disabled={saving} className="flex-1 bg-[#F2B705] text-black font-bold py-2.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="animate-spin" size={16} />} {isNew ? "Add Vehicle" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
